import jsPDF from "jspdf"
import "jspdf-autotable"
import { deliverPdf } from "./helpers/pdfDelivery.js"
import { drawPdfLogo } from "./helpers/pdfLogo.js"

const formatCurrency = (value) =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0)

const formatDate = (dateString) => {
  if (!dateString) return "N/A"
  const dateObj = new Date(dateString)
  if (Number.isNaN(dateObj.getTime())) return String(dateString)
  return new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(dateObj)
}

/**
 * Retorna explicación amigable para el usuario según tipo de transacción.
 * Basado en PROVEEDORES-GESTION-CTA-CTE.md sección 9.1 Clasificación.
 */
const getExplicacionTransaccion = (tx) => {
  const tipo = tx?.tipo || ""
  const esDevolucion = Boolean(tx?.esDevolucion)
  const revertido = Boolean(tx?.revertido)

  let explicacion = ""
  if (tipo === "Servicio") {
    explicacion = esDevolucion ? "Anulación de consumo de servicio" : "Servicio consumido en paquete"
  } else if (tipo === "Pago") {
    explicacion = esDevolucion ? "Reversión de pago (devolución/pérdida)" : "Adelanto o pago al proveedor"
  } else if (tipo === "Cta Cte") {
    explicacion = esDevolucion ? "Reversión a crédito" : "Uso de crédito de cuenta corriente"
  } else {
    explicacion = tipo || "-"
  }

  if (revertido && !esDevolucion) {
    explicacion += " (ya revertido)"
  }
  return explicacion
}

export const generarPdfProveedorDetalleCC = ({
  entidad,
  cuenta = {},
  transacciones = [],
  fechaDesde = "",
  fechaHasta = "",
  usuario = "Usuario",
  logoBase64 = null,
  deliveryContext = null,
}) => {
  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" })
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const margin = 20

  const PAL = {
    primary: [25, 118, 210],
    ink: [33, 33, 33],
    muted: [117, 117, 117],
    inkLight: [158, 158, 158],
    bgSoft: [245, 247, 250],
    success: [76, 175, 80],
    error: [244, 67, 54],
  }

  const nombreProveedor = entidad
    ? `${entidad.descripcion || ""} ${entidad.apellido || ""}`.trim() || "Proveedor"
    : "Proveedor"
  const montoAcumulado = Number(cuenta?.montoAcumulado) || 0
  const cobradoAcumulado = Number(cuenta?.cobradoAcumulado) || 0
  const saldoDinamico = Number(cuenta?.saldoDinamico) ?? montoAcumulado - cobradoAcumulado

  drawPdfLogo({ doc, logoBase64, x: margin, y: margin, maxW: 80, maxH: 36 })

  let yPosition = margin + 36

  // Título principal
  doc.setFont("helvetica", "bold")
  doc.setFontSize(16)
  doc.setTextColor(...PAL.primary)
  doc.text(`DETALLE CUENTA CORRIENTE - ${nombreProveedor}`, pageWidth / 2, yPosition, { align: "center" })
  yPosition += 20

  // Fecha de generación
  doc.setFont("helvetica", "normal")
  doc.setFontSize(9)
  doc.setTextColor(...PAL.inkLight)
  doc.text(
    `Fecha de generación: ${new Date().toLocaleString("es-AR")} | Usuario: ${usuario}`,
    pageWidth - margin,
    yPosition,
    { align: "right" }
  )
  yPosition += 24

  // Bloque Información del Proveedor
  doc.setFont("helvetica", "bold")
  doc.setFontSize(11)
  doc.setTextColor(...PAL.ink)
  doc.text("Información del Proveedor", margin, yPosition)
  yPosition += 14

  doc.setDrawColor(...PAL.primary)
  doc.setLineWidth(1)
  doc.line(margin, yPosition, pageWidth - margin, yPosition)
  yPosition += 12

  doc.setFont("helvetica", "normal")
  doc.setFontSize(9)
  doc.setTextColor(...PAL.muted)
  const infoLine =
    `Nombre: ${nombreProveedor} | DNI/CUIT/CUIL: ${entidad?.dniCuitCuil || "N/A"} | Teléfono: ${entidad?.telefono || "N/A"}`
  doc.text(infoLine, margin, yPosition, { maxWidth: pageWidth - margin * 2 })
  yPosition += 24

  // Bloque Resumen de Cuenta
  doc.setFont("helvetica", "bold")
  doc.setFontSize(11)
  doc.setTextColor(...PAL.ink)
  doc.text("Resumen de Cuenta", margin, yPosition)
  yPosition += 14

  doc.setDrawColor(...PAL.primary)
  doc.line(margin, yPosition, pageWidth - margin, yPosition)
  yPosition += 12

  doc.setFillColor(...PAL.bgSoft)
  doc.roundedRect(margin, yPosition - 5, pageWidth - margin * 2, 24, 3, 3, "F")
  doc.setFont("helvetica", "bold")
  doc.setFontSize(9)
  doc.setTextColor(...PAL.ink)
  doc.text(
    `Monto Acumulado: ${formatCurrency(montoAcumulado)} | Consumido: ${formatCurrency(cobradoAcumulado)} | Saldo: ${formatCurrency(saldoDinamico)}`,
    margin + 8,
    yPosition + 6
  )
  yPosition += 30

  // Período / Filtros
  doc.setFont("helvetica", "bold")
  doc.setFontSize(10)
  doc.setTextColor(...PAL.ink)
  doc.text(`Período: ${formatDate(fechaDesde) || "Inicio"} a ${formatDate(fechaHasta) || "Fin"}`, margin, yPosition)
  yPosition += 18

  // Tabla de transacciones
  const head = ["Fecha", "Tipo", "Qué significa", "Descripción", "Grupo", "Destino", "Monto", "Estado"]
  const body =
    transacciones.length === 0
      ? [["Sin transacciones en el período"]]
      : transacciones.map((tx) => [
          formatDate(tx.fecha),
          tx.tipo || "-",
          getExplicacionTransaccion(tx),
          tx.descripcion || "-",
          tx.grupo || "-",
          tx.destino || "-",
          formatCurrency(tx.monto),
          tx.revertido ? "Revertido" : "",
        ])

  const colCount = transacciones.length === 0 ? 1 : 8
  const colWidths = transacciones.length === 0
    ? { 0: { cellWidth: pageWidth - margin * 2 } }
    : {
        0: { cellWidth: 65, halign: "center" },
        1: { cellWidth: 55 },
        2: { cellWidth: 120, overflow: "linebreak" },
        3: { cellWidth: 100, overflow: "linebreak" },
        4: { cellWidth: 90, overflow: "linebreak" },
        5: { cellWidth: 90, overflow: "linebreak" },
        6: { cellWidth: 75, halign: "right" },
        7: { cellWidth: 55, halign: "center" },
      }

  doc.autoTable({
    startY: yPosition,
    head: transacciones.length === 0 ? [["Sin datos"]] : [head],
    body,
    theme: "grid",
    headStyles: {
      fillColor: PAL.primary,
      fontSize: 9,
      fontStyle: "bold",
      halign: "center",
      textColor: [255, 255, 255],
    },
    bodyStyles: {
      fontSize: 8,
      cellPadding: 4,
      textColor: PAL.ink,
    },
    alternateRowStyles: transacciones.length > 0 ? { fillColor: [250, 250, 250] } : undefined,
    columnStyles: colWidths,
    margin: { left: margin, right: margin },
    didDrawPage: (data) => {
      doc.setFont("helvetica", "normal")
      doc.setFontSize(8)
      doc.setTextColor(...PAL.inkLight)
      const pageText = `Página ${data.pageNumber} de ${doc.internal.pages.length - 1}`
      doc.text(pageText, pageWidth / 2, pageHeight - 20, { align: "center" })
    },
  })

  const fileName = `detalle-cc-${nombreProveedor.replace(/\s+/g, "-")}.pdf`
  doc.setProperties({ title: fileName, subject: "Detalle Cuenta Corriente Proveedor", creator: "Sistema" })
  deliverPdf({ doc, fileName, targetWindow: deliveryContext?.targetWindow || null })
  return true
}

/**
 * Genera un PDF de recibo/comprobante por transacción (una fila).
 * Explica qué sucedió en esa transacción para que el usuario entienda.
 */
export const generarPdfReciboTransaccionProveedor = ({
  entidad,
  tx,
  usuario = "Usuario",
  logoBase64 = null,
  deliveryContext = null,
}) => {
  if (!tx) return false

  const doc = new jsPDF({ unit: "pt", format: "a4" })
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const margin = 24

  const PAL = {
    primary: [25, 118, 210],
    ink: [33, 33, 33],
    muted: [117, 117, 117],
    inkLight: [158, 158, 158],
    bgSoft: [245, 247, 250],
    success: [76, 175, 80],
    error: [244, 67, 54],
  }

  const nombreProveedor = entidad
    ? `${entidad.descripcion || ""} ${entidad.apellido || ""}`.trim() || "Proveedor"
    : "Proveedor"

  drawPdfLogo({ doc, logoBase64, x: margin, y: margin, maxW: 80, maxH: 36 })

  let y = margin + 36

  doc.setFont("helvetica", "bold")
  doc.setFontSize(18)
  doc.setTextColor(...PAL.primary)
  doc.text("COMPROBANTE DE TRANSACCIÓN", pageWidth / 2, y, { align: "center" })
  y += 28

  doc.setFont("helvetica", "normal")
  doc.setFontSize(9)
  doc.setTextColor(...PAL.inkLight)
  doc.text(
    `Generado: ${new Date().toLocaleString("es-AR")} | Usuario: ${usuario}`,
    pageWidth - margin,
    y,
    { align: "right" }
  )
  y += 28

  doc.setDrawColor(...PAL.primary)
  doc.setLineWidth(1)
  doc.line(margin, y, pageWidth - margin, y)
  y += 20

  doc.setFont("helvetica", "bold")
  doc.setFontSize(11)
  doc.setTextColor(...PAL.ink)
  doc.text("Proveedor", margin, y)
  y += 12

  doc.setFont("helvetica", "normal")
  doc.setFontSize(10)
  doc.setTextColor(...PAL.muted)
  doc.text(nombreProveedor, margin, y)
  y += 24

  doc.setFont("helvetica", "bold")
  doc.setFontSize(10)
  doc.setTextColor(...PAL.ink)
  doc.text("Detalle de la operación", margin, y)
  y += 14

  const detalleRows = [
    ["Fecha", formatDate(tx.fecha)],
    ["Tipo", tx.tipo || "-"],
    ["Descripción", (tx.descripcion || "-").toString().slice(0, 80)],
    ["Grupo", (tx.grupo || "-").toString()],
    ["Destino", (tx.destino || "-").toString()],
    ["Monto", formatCurrency(tx.monto)],
    ["Estado", tx.revertido ? "Revertido" : "Activo"],
  ]

  doc.autoTable({
    startY: y,
    head: [["Campo", "Valor"]],
    body: detalleRows,
    theme: "grid",
    headStyles: {
      fillColor: PAL.primary,
      fontSize: 9,
      fontStyle: "bold",
      textColor: [255, 255, 255],
    },
    bodyStyles: { fontSize: 9, textColor: PAL.ink },
    columnStyles: {
      0: { cellWidth: 80, fontStyle: "bold" },
      1: { cellWidth: pageWidth - margin * 2 - 80, overflow: "linebreak" },
    },
    margin: { left: margin, right: margin },
  })

  y = doc.lastAutoTable.finalY + 20

  if (tx.revertido) {
    doc.setFont("helvetica", "italic")
    doc.setFontSize(9)
    doc.setTextColor(...PAL.muted)
    doc.text("Esta transacción fue revertida posteriormente.", margin, y)
    y += 14
  }

  doc.setFont("helvetica", "normal")
  doc.setFontSize(8)
  doc.setTextColor(...PAL.inkLight)
  doc.text(
    `Transacción ID: ${tx.id} | Cuenta Corriente Proveedor`,
    pageWidth / 2,
    pageHeight - 20,
    { align: "center" }
  )

  const tipoSlug = (tx.tipo || "tx").replace(/\s+/g, "-").toLowerCase()
  const fileName = `recibo-${tipoSlug}-${tx.id}-${nombreProveedor.replace(/\s+/g, "-")}.pdf`
  doc.setProperties({ title: fileName, subject: "Recibo Transacción Proveedor", creator: "Sistema" })
  deliverPdf({ doc, fileName, targetWindow: deliveryContext?.targetWindow || null })
  return true
}
